import { useState } from "react";
import axios from "axios";
import { type AnalysisEntry } from "@/lib/projectStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  BookOpen,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

type Step = "review" | "generating" | "edit" | "confirm";

interface AffectedSystem {
  _key: string;
  name: string;
  impact_level: "high" | "medium" | "low";
  description: string;
}

interface StakeholderImpact {
  _key: string;
  role: string;
  impact: string;
}

interface Risk {
  _key: string;
  description: string;
  severity: "high" | "medium" | "low";
  mitigation: string;
}

interface Dependency {
  _key: string;
  direction: "upstream" | "downstream";
  description: string;
}

interface Draft {
  summary: string;
  affected_systems: AffectedSystem[];
  stakeholders: StakeholderImpact[];
  risks: Risk[];
  dependencies: Dependency[];
  effort_estimate: "low" | "medium" | "high";
}

interface Props {
  requirement: any;
  onConfirm: (analysis: AnalysisEntry) => void;
  onClose: () => void;
}

const impactColors: Record<string, string> = {
  high: "bg-destructive/15 text-destructive border-destructive/30",
  medium: "bg-accent/15 text-accent border-accent/30",
  low: "bg-secondary text-secondary-foreground border-border",
};

const effortLabels: Record<string, string> = {
  low: "Low (<1 sprint)",
  medium: "Medium (1–3 sprints)",
  high: "High (>3 sprints)",
};

function makeKey() {
  return `k-${Date.now()}-${Math.random()}`;
}

export default function ImpactAnalysisWizard({ requirement, onConfirm, onClose }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [step, setStep] = useState<Step>("review");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ragContextUsed, setRagContextUsed] = useState(false);
  const [ragChunks, setRagChunks] = useState(0);

  // ─── Step 1 → 2: Generate ──────────────────────────────────────────────────

  const handleGenerate = async () => {
    setStep("generating");
    setError(null);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/impact-analysis`,
        { requirement_id: requirement.id },
        { headers: authHeader }
      );
      const a = data.analysis || {};
      setDraft({
        summary: a.summary || "",
        affected_systems: (a.affected_systems || []).map((s: any) => ({
          _key: makeKey(),
          name: s.name || "",
          impact_level: (["high", "medium", "low"].includes(s.impact_level) ? s.impact_level : "medium") as AffectedSystem["impact_level"],
          description: s.description || "",
        })),
        stakeholders: (a.stakeholders || []).map((s: any) => ({
          _key: makeKey(),
          role: s.role || "",
          impact: s.impact || "",
        })),
        risks: (a.risks || []).map((r: any) => ({
          _key: makeKey(),
          description: r.description || "",
          severity: (["high", "medium", "low"].includes(r.severity) ? r.severity : "medium") as Risk["severity"],
          mitigation: r.mitigation || "",
        })),
        dependencies: (a.dependencies || []).map((d: any) => ({
          _key: makeKey(),
          direction: (["upstream", "downstream"].includes(d.direction) ? d.direction : "upstream") as Dependency["direction"],
          description: d.description || "",
        })),
        effort_estimate: (["low", "medium", "high"].includes(a.effort_estimate) ? a.effort_estimate : "medium") as Draft["effort_estimate"],
      });
      setRagContextUsed(data.rag_context_used || false);
      setRagChunks(data.rag_chunks || 0);
      setStep("edit");
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Analysis failed");
      setStep("review");
    }
  };

  // ─── Edit helpers ──────────────────────────────────────────────────────────

  const updateSystem = (key: string, changes: Partial<AffectedSystem>) =>
    setDraft((d) => d ? { ...d, affected_systems: d.affected_systems.map((s) => s._key === key ? { ...s, ...changes } : s) } : d);
  const removeSystem = (key: string) =>
    setDraft((d) => d ? { ...d, affected_systems: d.affected_systems.filter((s) => s._key !== key) } : d);
  const addSystem = () =>
    setDraft((d) => d ? { ...d, affected_systems: [...d.affected_systems, { _key: makeKey(), name: "", impact_level: "medium", description: "" }] } : d);

  const updateStakeholder = (key: string, changes: Partial<StakeholderImpact>) =>
    setDraft((d) => d ? { ...d, stakeholders: d.stakeholders.map((s) => s._key === key ? { ...s, ...changes } : s) } : d);
  const removeStakeholder = (key: string) =>
    setDraft((d) => d ? { ...d, stakeholders: d.stakeholders.filter((s) => s._key !== key) } : d);
  const addStakeholder = () =>
    setDraft((d) => d ? { ...d, stakeholders: [...d.stakeholders, { _key: makeKey(), role: "", impact: "" }] } : d);

  const updateRisk = (key: string, changes: Partial<Risk>) =>
    setDraft((d) => d ? { ...d, risks: d.risks.map((r) => r._key === key ? { ...r, ...changes } : r) } : d);
  const removeRisk = (key: string) =>
    setDraft((d) => d ? { ...d, risks: d.risks.filter((r) => r._key !== key) } : d);
  const addRisk = () =>
    setDraft((d) => d ? { ...d, risks: [...d.risks, { _key: makeKey(), description: "", severity: "medium", mitigation: "" }] } : d);

  const updateDep = (key: string, changes: Partial<Dependency>) =>
    setDraft((d) => d ? { ...d, dependencies: d.dependencies.map((dep) => dep._key === key ? { ...dep, ...changes } : dep) } : d);
  const removeDep = (key: string) =>
    setDraft((d) => d ? { ...d, dependencies: d.dependencies.filter((dep) => dep._key !== key) } : d);
  const addDep = () =>
    setDraft((d) => d ? { ...d, dependencies: [...d.dependencies, { _key: makeKey(), direction: "upstream", description: "" }] } : d);

  // ─── Step 4: Confirm ───────────────────────────────────────────────────────

  const handleConfirm = () => {
    if (!draft) return;
    const now = new Date().toISOString();
    const entry: AnalysisEntry = {
      id: crypto.randomUUID(),
      techniqueId: "impact-analysis",
      techniqueName: `Impact Analysis: ${requirement.title}`,
      data: {
        summary: draft.summary,
        effort_estimate: draft.effort_estimate,
        affected_systems: draft.affected_systems
          .filter((s) => s.name.trim())
          .map((s) => `${s.name} (${s.impact_level}): ${s.description}`),
        stakeholders: draft.stakeholders
          .filter((s) => s.role.trim())
          .map((s) => `${s.role}: ${s.impact}`),
        risks: draft.risks
          .filter((r) => r.description.trim())
          .map((r) => `${r.severity.toUpperCase()}: ${r.description} — Mitigation: ${r.mitigation}`),
        dependencies: draft.dependencies
          .filter((d) => d.description.trim())
          .map((d) => `${d.direction}: ${d.description}`),
      },
      createdAt: now,
      updatedAt: now,
    };
    onConfirm(entry);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Wizard header */}
      <div className="border-b border-border px-4 py-3 flex items-center gap-3 shrink-0">
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className={step === "review" || step === "generating" ? "text-foreground font-semibold" : ""}>
            1. Review
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === "edit" ? "text-foreground font-semibold" : ""}>
            2. Edit Analysis
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === "confirm" ? "text-foreground font-semibold" : ""}>
            3. Confirm
          </span>
        </div>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground/60 hidden sm:block">
          {requirement.title}
        </span>
      </div>

      <div className="flex-1 overflow-auto">

        {/* ── STEP 1: Review requirement ────────────────────────────────────── */}
        {(step === "review" || step === "generating") && (
          <div className="p-6 max-w-2xl mx-auto space-y-5">
            <div>
              <h2 className="text-base font-semibold font-display text-foreground">
                Review Requirement
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Confirm the details below are accurate before the agent drafts an impact analysis. You will review and edit every field before anything saves.
              </p>
            </div>

            <div className="border border-border rounded-lg bg-card divide-y divide-border">
              <div className="px-4 py-3 flex items-start justify-between gap-3">
                <span className="text-sm font-semibold font-body text-card-foreground">
                  {requirement.title}
                </span>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  <Badge variant="outline" className="text-[10px] px-1.5">{requirement.req_type}</Badge>
                  <Badge variant="outline" className="text-[10px] px-1.5">{requirement.status}</Badge>
                  {requirement.contains_phi && (
                    <Badge variant="outline" className="text-[10px] px-1.5 bg-destructive/10 text-destructive border-destructive/30 gap-0.5">
                      <AlertTriangle className="h-2.5 w-2.5" /> PHI
                    </Badge>
                  )}
                </div>
              </div>

              {requirement.raw_text && (
                <div className="px-4 py-3">
                  <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Raw Notes</p>
                  <p className="text-xs text-foreground/80 whitespace-pre-wrap">{requirement.raw_text}</p>
                </div>
              )}

              <div className="px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-2">
                {[
                  ["Feature", requirement.feature],
                  ["Channel", requirement.channel],
                  ["Market", requirement.market],
                  ["Type", requirement.req_type],
                ].map(([label, value]) =>
                  value ? (
                    <div key={label}>
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{label}</p>
                      <p className="text-xs text-foreground">{value}</p>
                    </div>
                  ) : null
                )}
              </div>

              {requirement.sensitivity_flags?.length > 0 && (
                <div className="px-4 py-3">
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Sensitivity Flags</p>
                  <div className="flex flex-wrap gap-1">
                    {requirement.sensitivity_flags.map((f: string) => (
                      <Badge key={f} variant="outline" className="text-[10px] px-1.5 bg-amber-50 text-amber-700 border-amber-200">{f}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {error && (
              <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-muted-foreground max-w-sm">
                The AI will read this requirement and your project knowledge base to draft an impact analysis. You review everything before it saves.
              </p>
              <Button
                onClick={handleGenerate}
                disabled={step === "generating"}
                className="gap-2 shrink-0"
              >
                {step === "generating" ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analysing…</>
                ) : (
                  <><TrendingUp className="h-3.5 w-3.5" /> Run Impact Analysis</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Edit draft ────────────────────────────────────────────── */}
        {step === "edit" && draft && (
          <div className="p-6 max-w-3xl mx-auto space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold font-display text-foreground">Edit Impact Analysis</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  These are AI suggestions — edit freely. Nothing saves until you confirm.
                </p>
              </div>
              {ragContextUsed && (
                <div className="flex items-center gap-1 text-[11px] text-accent shrink-0 border border-accent/30 rounded px-2 py-1 bg-accent/5">
                  <BookOpen className="h-3 w-3" />
                  {ragChunks} policy chunk{ragChunks !== 1 ? "s" : ""} used
                </div>
              )}
            </div>

            {/* Summary */}
            <section className="space-y-2">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Summary</p>
              <Textarea
                value={draft.summary}
                onChange={(e) => setDraft((d) => d ? { ...d, summary: e.target.value } : d)}
                rows={3}
                placeholder="Overall impact summary…"
                className="text-sm"
              />
            </section>

            {/* Effort estimate */}
            <section className="space-y-2">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Effort Estimate</p>
              <Select
                value={draft.effort_estimate}
                onValueChange={(v) => setDraft((d) => d ? { ...d, effort_estimate: v as Draft["effort_estimate"] } : d)}
              >
                <SelectTrigger className="w-56 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low (&lt;1 sprint)</SelectItem>
                  <SelectItem value="medium">Medium (1–3 sprints)</SelectItem>
                  <SelectItem value="high">High (&gt;3 sprints)</SelectItem>
                </SelectContent>
              </Select>
            </section>

            {/* Affected systems */}
            <section className="space-y-3">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Affected Systems</p>
              {draft.affected_systems.map((s) => (
                <div key={s._key} className="border border-border rounded-lg p-3 space-y-2 bg-card">
                  <div className="flex items-center gap-2">
                    <Input
                      value={s.name}
                      onChange={(e) => updateSystem(s._key, { name: e.target.value })}
                      placeholder="System or component name"
                      className="flex-1 h-7 text-xs font-semibold"
                    />
                    <Select value={s.impact_level} onValueChange={(v) => updateSystem(s._key, { impact_level: v as AffectedSystem["impact_level"] })}>
                      <SelectTrigger className="w-28 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => removeSystem(s._key)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Input
                    value={s.description}
                    onChange={(e) => updateSystem(s._key, { description: e.target.value })}
                    placeholder="How is it affected?"
                    className="h-7 text-xs"
                  />
                </div>
              ))}
              <button onClick={addSystem} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg px-4 py-2.5 w-full transition-colors hover:border-foreground/30">
                <Plus className="h-3.5 w-3.5" /> Add system
              </button>
            </section>

            {/* Stakeholders */}
            <section className="space-y-3">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Stakeholder Impacts</p>
              {draft.stakeholders.map((s) => (
                <div key={s._key} className="border border-border rounded-lg p-3 flex gap-2 items-start bg-card">
                  <Input
                    value={s.role}
                    onChange={(e) => updateStakeholder(s._key, { role: e.target.value })}
                    placeholder="Role"
                    className="w-36 h-7 text-xs shrink-0 font-semibold"
                  />
                  <Input
                    value={s.impact}
                    onChange={(e) => updateStakeholder(s._key, { impact: e.target.value })}
                    placeholder="How are they impacted?"
                    className="flex-1 h-7 text-xs"
                  />
                  <button onClick={() => removeStakeholder(s._key)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0 pt-1">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={addStakeholder} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg px-4 py-2.5 w-full transition-colors hover:border-foreground/30">
                <Plus className="h-3.5 w-3.5" /> Add stakeholder
              </button>
            </section>

            {/* Risks */}
            <section className="space-y-3">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Risks</p>
              {draft.risks.map((r) => (
                <div key={r._key} className="border border-border rounded-lg p-3 space-y-2 bg-card">
                  <div className="flex items-center gap-2">
                    <Select value={r.severity} onValueChange={(v) => updateRisk(r._key, { severity: v as Risk["severity"] })}>
                      <SelectTrigger className="w-28 h-7 text-xs shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={r.description}
                      onChange={(e) => updateRisk(r._key, { description: e.target.value })}
                      placeholder="Risk description"
                      className="flex-1 h-7 text-xs"
                    />
                    <button onClick={() => removeRisk(r._key)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Input
                    value={r.mitigation}
                    onChange={(e) => updateRisk(r._key, { mitigation: e.target.value })}
                    placeholder="Suggested mitigation"
                    className="h-7 text-xs text-muted-foreground"
                  />
                </div>
              ))}
              <button onClick={addRisk} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg px-4 py-2.5 w-full transition-colors hover:border-foreground/30">
                <Plus className="h-3.5 w-3.5" /> Add risk
              </button>
            </section>

            {/* Dependencies */}
            <section className="space-y-3">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Dependencies</p>
              {draft.dependencies.map((d) => (
                <div key={d._key} className="border border-border rounded-lg p-3 flex gap-2 items-center bg-card">
                  <Select value={d.direction} onValueChange={(v) => updateDep(d._key, { direction: v as Dependency["direction"] })}>
                    <SelectTrigger className="w-32 h-7 text-xs shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upstream">Upstream</SelectItem>
                      <SelectItem value="downstream">Downstream</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={d.description}
                    onChange={(e) => updateDep(d._key, { description: e.target.value })}
                    placeholder="Dependency description"
                    className="flex-1 h-7 text-xs"
                  />
                  <button onClick={() => removeDep(d._key)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={addDep} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg px-4 py-2.5 w-full transition-colors hover:border-foreground/30">
                <Plus className="h-3.5 w-3.5" /> Add dependency
              </button>
            </section>

            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => setStep("review")}>Back</Button>
              <Button size="sm" onClick={() => setStep("confirm")} className="gap-1.5">
                Review & Confirm <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Confirm ───────────────────────────────────────────────── */}
        {step === "confirm" && draft && (
          <div className="p-6 max-w-2xl mx-auto space-y-5">
            <div>
              <h2 className="text-base font-semibold font-display text-foreground">Confirm Impact Analysis</h2>
              <p className="text-xs text-muted-foreground mt-1">
                This analysis will be saved to the Analysis tab. You can edit it there at any time.
              </p>
            </div>

            <div className="border border-border rounded-lg bg-card divide-y divide-border">
              {draft.summary && (
                <div className="px-4 py-3">
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Summary</p>
                  <p className="text-xs text-foreground/80">{draft.summary}</p>
                </div>
              )}
              <div className="px-4 py-3">
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Effort Estimate</p>
                <Badge variant="outline" className={`text-[10px] px-1.5 ${impactColors[draft.effort_estimate === "high" ? "high" : draft.effort_estimate === "medium" ? "medium" : "low"]}`}>
                  {effortLabels[draft.effort_estimate]}
                </Badge>
              </div>
              {draft.affected_systems.filter((s) => s.name.trim()).length > 0 && (
                <div className="px-4 py-3 space-y-1.5">
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Affected Systems</p>
                  {draft.affected_systems.filter((s) => s.name.trim()).map((s) => (
                    <div key={s._key} className="flex items-start gap-2">
                      <Badge variant="outline" className={`text-[10px] px-1.5 shrink-0 ${impactColors[s.impact_level]}`}>{s.impact_level}</Badge>
                      <p className="text-xs text-foreground"><span className="font-semibold">{s.name}</span>{s.description && `: ${s.description}`}</p>
                    </div>
                  ))}
                </div>
              )}
              {draft.risks.filter((r) => r.description.trim()).length > 0 && (
                <div className="px-4 py-3 space-y-1.5">
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Risks</p>
                  {draft.risks.filter((r) => r.description.trim()).map((r) => (
                    <div key={r._key} className="flex items-start gap-2">
                      <Badge variant="outline" className={`text-[10px] px-1.5 shrink-0 ${impactColors[r.severity]}`}>{r.severity}</Badge>
                      <p className="text-xs text-foreground">{r.description}{r.mitigation && <span className="text-muted-foreground"> — {r.mitigation}</span>}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-border rounded-lg bg-secondary/20 px-4 py-3 text-xs text-muted-foreground space-y-0.5">
              <p className="font-semibold text-foreground">What happens next</p>
              <p>· The analysis is added to the Analysis tab under your project.</p>
              <p>· You can edit or delete it there at any time.</p>
              <p>· This action is logged in the Agent Log tab.</p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button variant="outline" size="sm" onClick={() => setStep("edit")}>Back to Edit</Button>
              <Button onClick={handleConfirm} className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Save Impact Analysis
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
